import { Location } from '@angular/common';
import { EventEmitter, Injectable, OnDestroy, inject } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { HubConnection, HubConnectionBuilder, HubConnectionState, IHubProtocol } from '@microsoft/signalr';
import { MessagePackHubProtocol } from '@microsoft/signalr-protocol-msgpack';
import { Subscription } from 'rxjs';
import { AuthorisationService } from './authorisation.service';
import { ConfigurationService } from './configuration.service';

@Injectable({ providedIn: 'root' })
export class AppSignalRService implements OnDestroy
{
  private authorisationService: AuthorisationService = inject(AuthorisationService);
  private configurationService: ConfigurationService = inject(ConfigurationService);

  public connectionEstablished = new EventEmitter<boolean>();
  public startConnectionTimeoutDelay = 3000;
  public protocol: IHubProtocol = new MessagePackHubProtocol();
  public autoReconnect = true;

  private _userId = 0;
  private _connectionIsEstablished = false;
  private _hubConnection: HubConnection;
  private _starting = false; // prevents concurrent starts

  private connectedSubscription: Subscription;

  constructor(public location: Location) { }

  createConnection(huburl: string, userId: number)
  {
    this._userId = userId;

    if (!this._hubConnection && this._userId > 0)
    {
      const hubConnectionBuilder: HubConnectionBuilder = new HubConnectionBuilder();

      hubConnectionBuilder.withUrl(huburl, {
        withCredentials: this.authorisationService.access_token() != null && this.authorisationService.access_token() != "",
        accessTokenFactory: () =>
        {
          let token = this.authorisationService.access_token();
          return token ?? '';
        },
        skipNegotiation: false
      });
      hubConnectionBuilder.withHubProtocol(this.protocol);
      hubConnectionBuilder.configureLogging(this.configurationService.cbSettings().signalRLogLevel);

      if (this.autoReconnect)
      {
        hubConnectionBuilder.withAutomaticReconnect();
      }

      this._hubConnection = hubConnectionBuilder.build();

      // Only manually restart if autoReconnect is disabled
      this.hubConnection.onclose((msg) =>
      {
        console.warn(msg?.message || 'Hub connection closed');
        if (!this.autoReconnect)
        {
          this.startConnection();
        }
      });

      this.hubConnection.onreconnecting(error =>
      {
        console.warn('Hub reconnecting...', error?.message);
        this._connectionIsEstablished = false;
      });

      this.hubConnection.onreconnected((connectionId: string) =>
      {
        console.log('Hub reconnected', connectionId);
        this.hubConnection.invoke('RegisterConnection', this._userId);
        this._connectionIsEstablished = true;
        this.connectionEstablished.emit(true);
      });
    }
  }

  startConnection()
  {
    if (!this._hubConnection)
    {
      console.warn('Hub connection not yet created. Call createConnection first.');
      return;
    }

    if (this._hubConnection.state !== HubConnectionState.Disconnected || this._starting)
    {
      // Already started / starting / reconnecting
      return;
    }

    this._starting = true;

    this._hubConnection
      .start()
      .then(() =>
      {
        this._starting = false;
        this._connectionIsEstablished = true;
        console.log('Hub connection started');
        this.connectionEstablished.emit(true);

        this.hubConnection.invoke('RegisterConnection', this._userId);
      })
      .catch(err =>
      {
        this._starting = false;
        this._connectionIsEstablished = false;
        console.log('Error while establishing connection, retrying...');
        console.error(err?.message || err);

        setTimeout(() =>
        {
          this.startConnection();
        }, this.startConnectionTimeoutDelay);
      });
  }

  // ...args: any[] allows you to pass any number of arguments
  run(method: string, ...args: any[])
  {
    if (!this._hubConnection)
    {
      console.warn('Hub connection not created. Ignoring call to', method);
      return;
    }

    switch (this._hubConnection.state)
    {
      case HubConnectionState.Connected:
        this._hubConnection.invoke(method, ...args);
        break;

      case HubConnectionState.Connecting:
      case HubConnectionState.Reconnecting:
        // Queue the invoke until connected
        this.connectedSubscription = this.connectionEstablished.subscribe(() =>
        {
          this._hubConnection?.invoke(method, ...args);
          this.connectedSubscription?.unsubscribe();
        });
        break;

      case HubConnectionState.Disconnected:
        // Attempt a (guarded) start then queue invoke
        this.startConnection();
        this.connectedSubscription = this.connectionEstablished.subscribe(() =>
        {
          this._hubConnection?.invoke(method, ...args);
          this.connectedSubscription?.unsubscribe();
        });
        break;
    }
  }

  get connectionIsEstablished(): boolean
  {
    return this._connectionIsEstablished;
  }

  get hubConnection(): HubConnection
  {
    return this._hubConnection;
  }

  ngOnDestroy()
  {
    if (this.connectedSubscription)
    {
      // prevent memory leak when service destroyed
      this.connectedSubscription.unsubscribe();
    }
  }
}
